import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Key, X as XIcon, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/**
 * ChangePasswordModal
 * - When `forced` is true (user.must_change_password === true after first login),
 *   the modal cannot be dismissed until the password is rotated.
 * - When `forced` is false, it acts as a normal user-initiated rotation dialog.
 */
export default function ChangePasswordModal({ open, onClose, forced = false }) {
  const { refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setOk(true);
      await refresh(); // updates user.must_change_password to false
      setTimeout(() => {
        setOk(false);
        setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
        if (onClose) onClose();
      }, 1200);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Password change failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="change-password-modal"
    >
      <div className="bg-white dark:bg-[var(--span-bg-2)] border border-[var(--span-border)] rounded-md max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--span-border)]">
          <div className="flex items-center gap-2">
            <Key size={18} weight="bold" />
            <h2 className="font-display font-bold text-[16px]" data-testid="change-password-title">
              {forced ? "Set your password" : "Change password"}
            </h2>
          </div>
          {!forced && (
            <button
              data-testid="change-password-close"
              onClick={onClose}
              className="text-[var(--span-text-2)] hover:text-[var(--span-text)]"
              aria-label="Close"
            >
              <XIcon size={18} />
            </button>
          )}
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {forced && (
            <div className="text-[12px] text-[var(--span-text-2)] bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-3 leading-relaxed" data-testid="forced-rotation-notice">
              <strong className="text-amber-700 dark:text-amber-300">First-time login.</strong>{" "}
              Please replace the temporary password{" "}
              <code className="font-mono text-[11px]">SpanWelcome@2026</code> with a private one. You
              will not be able to use the platform until this is done.
            </div>
          )}

          <div>
            <label className="label-eyebrow block mb-1.5">Current password</label>
            <input
              data-testid="cp-current"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="span-input font-mono text-[13px]"
              autoComplete="current-password"
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">New password</label>
            <input
              data-testid="cp-new"
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="span-input font-mono text-[13px]"
              autoComplete="new-password"
            />
            <div className="text-[10px] text-[var(--span-text-2)] mt-1 font-mono">
              Minimum 8 characters. Use a mix of letters, numbers and symbols.
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Confirm new password</label>
            <input
              data-testid="cp-confirm"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="span-input font-mono text-[13px]"
              autoComplete="new-password"
            />
          </div>

          {err && (
            <div className="flex items-start gap-2 text-[12px] text-[var(--span-danger)]" data-testid="cp-error">
              <WarningCircle size={14} weight="bold" className="mt-[2px] shrink-0" />
              <span>{err}</span>
            </div>
          )}
          {ok && (
            <div className="flex items-start gap-2 text-[12px] text-emerald-600 dark:text-emerald-400" data-testid="cp-success">
              <CheckCircle size={14} weight="bold" className="mt-[2px] shrink-0" />
              <span>Password updated.</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            {!forced && (
              <button
                type="button"
                data-testid="cp-cancel"
                onClick={onClose}
                className="span-btn span-btn-ghost"
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              data-testid="cp-submit"
              disabled={loading || ok}
              className="span-btn disabled:opacity-50"
            >
              {loading ? "Updating…" : ok ? "Updated ✓" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
